import type { TableColumnDescriptor, TableDescriptor } from '@finance/contracts';
import type { TableState } from './table-state.js';
import { getVisibleOrderedColumns } from './table-state.js';

export type LocalTableExportScope = 'page' | 'selection' | 'all';

export interface LocalTableExportResult {
  allowed: boolean;
  reason?: 'SERVER_EXPORT_REQUIRED' | 'AUDITED_EXPORT_REQUIRED';
  headers: string[];
  rows: Record<string, unknown>[];
}

function exportValue(
  value: unknown,
  field: string,
  redactionPolicy: 'none' | 'mask' | 'omit' | undefined,
  redactFields: ReadonlySet<string>
): unknown {
  if (redactionPolicy === 'omit') return undefined;
  if (redactionPolicy === 'mask' || redactFields.has(field)) return '[REDACTED]';
  return value;
}

/**
 * Returns every exportable field already present in the authorized resolved
 * descriptor, ordered by the user's table column order but independent of
 * current visibility. Maintenance/internal-only columns opt out explicitly via
 * exportConfig.exportable=false or redactionPolicy=omit.
 */
export function getOrderedExportableColumns(
  contract: TableDescriptor,
  state: TableState,
): TableColumnDescriptor[] {
  const order = new Map(state.columnOrder.map((entry, index) => [entry.columnId, index]));
  const descriptorOrder = new Map(contract.columns.map((column, index) => [column.id, index]));
  return [...contract.columns]
    .filter(
      (column) =>
        column.exportConfig?.exportable !== false &&
        column.exportConfig?.redactionPolicy !== 'omit',
    )
    .sort((left, right) => {
      const leftOrder = order.get(left.id) ?? descriptorOrder.get(left.id) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = order.get(right.id) ?? descriptorOrder.get(right.id) ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    });
}

export function getVisibleExportableColumns(
  contract: TableDescriptor,
  state: TableState,
): TableColumnDescriptor[] {
  return getVisibleOrderedColumns(contract, state).filter(
    (column) =>
      column.exportConfig?.exportable !== false &&
      column.exportConfig?.redactionPolicy !== 'omit',
  );
}

/**
 * Builds an in-memory CSV-safe projection from the already-authorized current
 * page. All-matching exports remain server-owned. Field selection may include
 * any exportable business field in the resolved descriptor, even if that field
 * is currently hidden in the table view.
 */
export function buildLocalTableExport(
  contract: TableDescriptor,
  tableData: Record<string, unknown>[],
  state: TableState,
  getRowIdValue: (row: Record<string, unknown>) => string | undefined,
  scope: LocalTableExportScope,
  fieldOrder?: string[]
): LocalTableExportResult {
  if (scope === 'all') {
    return { allowed: false, reason: 'SERVER_EXPORT_REQUIRED', headers: [], rows: [] };
  }
  if (contract.privacyAndExportPolicy?.auditExports) {
    return { allowed: false, reason: 'AUDITED_EXPORT_REQUIRED', headers: [], rows: [] };
  }

  const redactFields = new Set(contract.privacyAndExportPolicy?.redactFields ?? []);
  const exportableColumns = getOrderedExportableColumns(contract, state);
  const columnMap = new Map(exportableColumns.map(column => [column.field, column]));
  const columns =
    fieldOrder && fieldOrder.length > 0
      ? fieldOrder
          .map(field => columnMap.get(field))
          .filter((column): column is TableColumnDescriptor => Boolean(column))
      : getVisibleExportableColumns(contract, state);
  const selectedIds = new Set(state.selection.selectedIds);
  const sourceRows =
    scope === 'selection'
      ? tableData.filter(row => {
          const id = getRowIdValue(row);
          return id !== undefined && selectedIds.has(id);
        })
      : tableData;

  return {
    allowed: true,
    headers: columns.map(column => column.exportConfig?.exportKey ?? column.field),
    rows: sourceRows.map(row =>
      Object.fromEntries(
        columns.map(column => [
          column.exportConfig?.exportKey ?? column.field,
          exportValue(
            row[column.field],
            column.field,
            column.exportConfig?.redactionPolicy,
            redactFields
          ),
        ])
      )
    ),
  };
}
